import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { supabase } from "../../lib/supabase";
import { useCategories } from "../../hooks/useCategories";
import { useApp } from "../../contexts/AppContext";

const LEVELS: { value: string; label: string }[] = [
  { value: "all", label: "すべて" },
  { value: "beginner", label: "入門" },
  { value: "intermediate", label: "中級" },
  { value: "advanced", label: "上級" },
];

const SORTS = [
  { value: "popular", label: "人気順" },
  { value: "newest", label: "新着順" },
  { value: "price_asc", label: "価格：安い順" },
  { value: "price_desc", label: "価格：高い順" },
  { value: "rating", label: "評価順" },
];

const PRICE_RANGES = [
  { label: "すべて", min: 0, max: Infinity },
  { label: "無料", min: 0, max: 0 },
  { label: "〜5,000円", min: 1, max: 5000 },
  { label: "5,001〜10,000円", min: 5001, max: 10000 },
  { label: "10,001〜20,000円", min: 10001, max: 20000 },
  { label: "20,001円〜", min: 20001, max: Infinity },
];

const levelLabel: Record<string, string> = {
  beginner: "入門",
  intermediate: "中級",
  advanced: "上級",
};

const levelColor: Record<string, string> = {
  beginner: "bg-teal/10 text-teal",
  intermediate: "bg-gold/10 text-gold",
  advanced: "bg-deep-green/10 text-deep-green",
};

interface VideoProductDB {
  id: string;
  title: string;
  description: string | null;
  price: number;
  thumbnail_url: string | null;
  category: string | null;
  total_minutes: number | null;
  difficulty: string | null;
  sales_count: number;
  is_free_sample: boolean;
  created_at: string;
  provider_id: string;
  provider: { id: string; name: string; service_name: string | null; rating: number; review_count: number } | null;
}

export default function CoursesPage() {
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedPriceIdx, setSelectedPriceIdx] = useState(0);
  const [sort, setSort] = useState("popular");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [products, setProducts] = useState<VideoProductDB[]>([]);
  const [loading, setLoading] = useState(true);
  const { openReport } = useApp();
  const { categories } = useCategories();

  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("products")
        .select("id,title,description,price,thumbnail_url,category,total_minutes,difficulty,sales_count,is_free_sample,created_at,provider_id,provider:provider_id(id,name,service_name,rating,review_count)")
        .eq("type", "video")
        .eq("visibility", "public")
        .eq("status", "public")
        .order("sales_count", { ascending: false });
      setProducts((data || []) as VideoProductDB[]);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          (v.provider?.service_name || v.provider?.name || "").toLowerCase().includes(q) ||
          (v.category || "").toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== "all") {
      list = list.filter((v) => v.category === selectedCategory);
    }
    if (selectedLevel !== "all") {
      list = list.filter((v) => v.difficulty === selectedLevel);
    }
    const pr = PRICE_RANGES[selectedPriceIdx];
    list = list.filter((v) => v.price >= pr.min && v.price <= pr.max);
    if (sort === "popular") list.sort((a, b) => b.sales_count - a.sales_count);
    else if (sort === "newest") list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    else if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, search, selectedCategory, selectedLevel, selectedPriceIdx, sort]);

  const getProviderName = (p: VideoProductDB) =>
    p.provider?.service_name || p.provider?.name || "事業者";
  const getDuration = (mins: number | null) => {
    if (!mins) return "";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}時間${m > 0 ? m + "分" : ""}` : `${m}分`;
  };

  return (
    <div className={`min-h-screen flex flex-col bg-ivory transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
      <Navbar />
      <section className="relative pt-16 overflow-hidden">
        <div className="h-48 md:h-56 bg-deep-green relative flex items-center justify-center">
          <div className="absolute inset-0 opacity-10">
            <img src="https://public.readdy.ai/ai/img_res/a10c322343d3171f9efda33a69f06dfd.jpg" className="w-full h-full object-cover" alt="" />
          </div>
          <div className="relative text-center px-4">
            <p className="font-display italic text-gold text-xs tracking-widest mb-3">Video Courses</p>
            <h1 className="font-serif text-white text-3xl md:text-4xl font-light mb-2">講座一覧</h1>
            <p className="text-white/60 text-sm font-sans">職人・専門家が直接指導する動画講座を探してみましょう</p>
          </div>
        </div>
      </section>

      <main className="max-w-screen-xl mx-auto w-full px-4 md:px-8 py-10 flex-1">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="講座名・事業者名・キーワードで検索"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 text-sm font-sans bg-white focus:outline-none focus:border-teal transition-colors" />
          </div>
          <div className="flex gap-2">
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="px-4 py-3 rounded-xl border border-stone-200 text-sm font-sans bg-white cursor-pointer focus:outline-none focus:border-teal">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <div className="flex rounded-xl border border-stone-200 overflow-hidden">
              <button onClick={() => setViewMode("grid")}
                className={`w-10 h-full flex items-center justify-center cursor-pointer transition-colors ${viewMode === "grid" ? "bg-deep-green text-white" : "bg-white text-charcoal/40 hover:text-charcoal"}`}>
                <i className="ri-grid-line text-sm" />
              </button>
              <button onClick={() => setViewMode("list")}
                className={`w-10 h-full flex items-center justify-center cursor-pointer transition-colors ${viewMode === "list" ? "bg-deep-green text-white" : "bg-white text-charcoal/40 hover:text-charcoal"}`}>
                <i className="ri-list-unordered text-sm" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-24 space-y-6">
              <div>
                <p className="text-xs font-sans text-charcoal/40 uppercase tracking-widest mb-3">カテゴリ</p>
                <div className="space-y-1">
                  <button onClick={() => setSelectedCategory("all")}
                    className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${selectedCategory === "all" ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                    すべて
                  </button>
                  {categories.map((c) => (
                    <button key={c.id} onClick={() => setSelectedCategory(c.name)}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${selectedCategory === c.name ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-sans text-charcoal/40 uppercase tracking-widest mb-3">難易度</p>
                <div className="space-y-1">
                  {LEVELS.map((l) => (
                    <button key={l.value} onClick={() => setSelectedLevel(l.value)}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${selectedLevel === l.value ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-sans text-charcoal/40 uppercase tracking-widest mb-3">価格帯</p>
                <div className="space-y-1">
                  {PRICE_RANGES.map((pr, i) => (
                    <button key={pr.label} onClick={() => setSelectedPriceIdx(i)}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg font-sans cursor-pointer transition-colors ${selectedPriceIdx === i ? "bg-deep-green text-white" : "text-charcoal/70 hover:bg-stone-100"}`}>
                      {pr.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="lg:hidden flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
              {LEVELS.map((l) => (
                <button key={l.value} onClick={() => setSelectedLevel(l.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-sans whitespace-nowrap cursor-pointer border transition-colors ${selectedLevel === l.value ? "bg-deep-green text-white border-deep-green" : "border-stone-200 text-charcoal/60 hover:border-stone-400"}`}>
                  {l.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mb-5">
              <p className="text-sm font-sans text-charcoal/50">
                {loading ? "読み込み中..." : <><span className="text-charcoal font-medium">{filtered.length}</span> 件の講座</>}
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-stone-100 overflow-hidden animate-pulse">
                    <div className="h-44 bg-stone-200" />
                    <div className="p-5 space-y-3">
                      <div className="h-3 bg-stone-200 rounded w-1/3" />
                      <div className="h-4 bg-stone-200 rounded w-3/4" />
                      <div className="h-3 bg-stone-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
                <i className="ri-video-line text-4xl text-charcoal/20 mb-4 block" />
                <p className="font-serif text-charcoal/40 text-lg">
                  {products.length === 0 ? "まだ講座が登録されていません" : "該当する講座がありません"}
                </p>
                {products.length === 0 && (
                  <Link to="/providers" className="mt-4 inline-block text-teal text-sm font-sans hover:underline cursor-pointer">事業者を探す →</Link>
                )}
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((course, idx) => {
                  const thumbnail = course.thumbnail_url || `https://readdy.ai/api/search-image?query=Japanese%20craft%20workshop%20tutorial%20artisan%20teaching%20professional%20minimal%20elegant&width=400&height=260&seq=crs-${course.id.slice(0,8)}&orientation=landscape`;
                  return (
                    <div key={course.id} className="bg-white rounded-2xl border border-stone-100 overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
                      style={{ animationDelay: `${idx * 60}ms` }}>
                      <Link to={`/courses/${course.id}`} className="block relative overflow-hidden h-44">
                        <img src={thumbnail} alt={course.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-deep-green/60 to-transparent" />
                        <div className="absolute top-3 left-3 flex gap-1.5">
                          {course.is_free_sample && <span className="text-xs bg-teal text-white px-2 py-0.5 rounded-full font-sans">無料</span>}
                          {course.difficulty && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${levelColor[course.difficulty] || "bg-stone-100 text-charcoal/50"}`}>
                              {levelLabel[course.difficulty] || course.difficulty}
                            </span>
                          )}
                        </div>
                        {course.total_minutes && (
                          <div className="absolute bottom-3 right-3">
                            <span className="bg-black/60 text-white text-xs px-2 py-0.5 rounded-full font-sans">
                              <i className="ri-time-line mr-1" />{getDuration(course.total_minutes)}
                            </span>
                          </div>
                        )}
                      </Link>
                      <div className="p-5">
                        <Link to={`/providers/${course.provider_id}`} className="text-xs text-teal font-sans hover:underline cursor-pointer">
                          {getProviderName(course)}
                        </Link>
                        <Link to={`/courses/${course.id}`}>
                          <h3 className="font-serif text-deep-green text-base mt-1 mb-3 line-clamp-2 hover:text-teal transition-colors leading-snug">{course.title}</h3>
                        </Link>
                        <div className="flex items-center justify-between">
                          <p className="font-serif text-deep-green text-lg">
                            {course.price === 0 ? <span className="text-teal">無料</span> : <>¥{course.price.toLocaleString()}</>}
                          </p>
                          <div className="flex gap-1">
                            <button onClick={(e) => { e.preventDefault(); openReport({ type: "video", id: course.id, title: course.title }); }}
                              className="w-7 h-7 flex items-center justify-center text-charcoal/20 hover:text-red-400 cursor-pointer transition-colors" title="通報する">
                              <i className="ri-flag-line text-sm" />
                            </button>
                            <Link to={`/courses/${course.id}`}
                              className="text-xs bg-deep-green text-white px-3 py-1.5 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
                              詳細を見る
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((course) => {
                  const thumbnail = course.thumbnail_url || `https://readdy.ai/api/search-image?query=Japanese%20craft%20tutorial%20artisan%20professional&width=160&height=90&seq=crs-l-${course.id.slice(0,8)}&orientation=landscape`;
                  return (
                    <div key={course.id} className="bg-white rounded-2xl border border-stone-100 p-4 flex gap-4 hover:border-teal/30 transition-colors">
                      <Link to={`/courses/${course.id}`} className="shrink-0 w-32 h-20 rounded-xl overflow-hidden">
                        <img src={thumbnail} alt={course.title} className="w-full h-full object-cover object-top" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <Link to={`/providers/${course.provider_id}`} className="text-xs text-teal font-sans hover:underline cursor-pointer">{getProviderName(course)}</Link>
                            <Link to={`/courses/${course.id}`}>
                              <h3 className="font-serif text-deep-green text-sm mt-0.5 hover:text-teal transition-colors line-clamp-2">{course.title}</h3>
                            </Link>
                          </div>
                          <p className="font-serif text-deep-green text-base shrink-0">
                            {course.price === 0 ? <span className="text-teal text-sm">無料</span> : `¥${course.price.toLocaleString()}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          {course.difficulty && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${levelColor[course.difficulty] || "bg-stone-100 text-charcoal/50"}`}>
                              {levelLabel[course.difficulty] || course.difficulty}
                            </span>
                          )}
                          {course.total_minutes && (
                            <span className="text-xs text-charcoal/40 font-sans flex items-center gap-1">
                              <i className="ri-time-line" />{getDuration(course.total_minutes)}
                            </span>
                          )}
                          <span className="text-xs text-charcoal/40 font-sans">{course.sales_count}人受講</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
